import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [denomination, setDenomination] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setAuthorized(false);
          setChecking(false);
          return;
        }

        const { data: user, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (error || !user) {
          setAuthorized(false);
        } else {
          setAuthorized(user.role === 'super_admin');
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    checkAuthorization();
  }, []);

  const generateCards = async () => {
    setLoading(true);
    setError('');
    setGeneratedCards([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }

      // Determine which denomination to use
      let finalDenomination;
      let isCustom = false;

      if (useCustomAmount) {
        const customValue = parseFloat(customAmount);
        
        if (isNaN(customValue) || customValue <= 0) {
          setError('Please enter a valid positive number for custom amount');
          setLoading(false);
          return;
        }
        
        if (customValue > 10000) {
          setError('Custom amount cannot exceed $10,000');
          setLoading(false);
          return;
        }

        finalDenomination = customValue;
        isCustom = true;
      } else {
        finalDenomination = denomination;
        isCustom = false;
      }

      const response = await fetch('https://globalpay-backend-v2.onrender.com/api/cards/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          denomination: finalDenomination, 
          quantity,
          isCustomDenomination: isCustom
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response. Backend might be down or rate limited.');
      }

      const data = await response.json();

      if (data.success) {
        setGeneratedCards(data.data.cards);
        alert(`Successfully generated ${quantity} card(s) worth $${finalDenomination} each!`);
      } else {
        setError(data.error || 'Failed to generate cards');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate cards');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ textAlign: 'center', color: 'white' }}><h2>🔍 Verifying Access...</h2></div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #fc8181 0%, #e53e3e 100%)' }}>
        <div style={{ textAlign: 'center', color: 'white', maxWidth: '500px', padding: '40px' }}>
          <h1 style={{ fontSize: '72px', margin: 0 }}>🚫</h1>
          <h2>Access Denied</h2>
          <p>You do not have permission to access this area.</p>
          <button onClick={() => navigate('/')} style={{ marginTop: '20px', padding: '12px 30px', fontSize: '16px', fontWeight: 'bold', borderRadius: '8px', border: 'none', background: 'white', color: '#e53e3e', cursor: 'pointer' }}>Go Back Home</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', minHeight: '100vh', background: '#f7fafc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#667eea' }}>🎫 Card Generation Admin Panel</h1>
        <button onClick={() => navigate('/')} style={{ padding: '10px 20px', background: '#718096', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Back to Dashboard</button>
      </div>
      
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '30px', borderRadius: '12px', marginBottom: '30px', color: 'white' }}>
        <h2>Generate New Cards</h2>
        
        {/* Toggle between standard and custom denominations */}
        <div style={{ marginBottom: '20px', padding: '15px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '10px' }}>
            <input 
              type="radio" 
              checked={!useCustomAmount} 
              onChange={() => setUseCustomAmount(false)}
              style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Standard Denominations ($50, $100, $200, $500)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input 
              type="radio" 
              checked={useCustomAmount} 
              onChange={() => setUseCustomAmount(true)}
              style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>🎨 Custom Amount (Any value)</span>
          </label>
        </div>

        {/* Standard denomination selector */}
        {!useCustomAmount && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Denomination:</label>
            <select 
              value={denomination} 
              onChange={(e) => setDenomination(Number(e.target.value))} 
              style={{ padding: '10px', fontSize: '16px', borderRadius: '8px', border: 'none', width: '200px', color: '#2d3748', background: 'white' }}
            >
              <option value={50}>$50</option>
              <option value={100}>$100</option>
              <option value={200}>$200</option>
              <option value={500}>$500</option>
            </select>
            <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.9 }}>
              ℹ️ Standard cards use 4-digit prefixes (0050, 0100, 0200, 0500)
            </div>
          </div>
        )}

        {/* Custom amount input */}
        {useCustomAmount && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Custom Amount (USD):</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px', fontWeight: 'bold' }}>$</span>
              <input 
                type="number" 
                min="0.01" 
                max="10000"
                step="0.01"
                value={customAmount} 
                onChange={(e) => setCustomAmount(e.target.value)} 
                placeholder="Enter amount (e.g., 5, 7.50, 23)"
                style={{ padding: '10px', fontSize: '16px', borderRadius: '8px', border: 'none', width: '200px', color: '#2d3748', background: 'white' }}
              />
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.9 }}>
              ℹ️ Custom cards use fully random 16-digit numbers (no prefix pattern)
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', opacity: 0.8 }}>
              💡 Examples: $5, $7.50, $15, $23.75, $99 - any amount up to $10,000
            </div>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Quantity (1-100):</label>
          <input 
            type="number" 
            min="1" 
            max="100" 
            value={quantity} 
            onChange={(e) => setQuantity(Number(e.target.value))} 
            style={{ padding: '10px', fontSize: '16px', borderRadius: '8px', border: 'none', width: '200px', color: '#2d3748', background: 'white' }} 
          />
        </div>

        <button 
          onClick={generateCards} 
          disabled={loading || (useCustomAmount && (!customAmount || parseFloat(customAmount) <= 0))} 
          style={{ 
            padding: '12px 30px', 
            fontSize: '16px', 
            fontWeight: 'bold', 
            borderRadius: '8px', 
            border: 'none', 
            background: loading ? '#ccc' : '#48bb78', 
            color: 'white', 
            cursor: loading ? 'not-allowed' : 'pointer', 
            transition: 'all 0.3s' 
          }}
        >
          {loading ? 'Generating...' : `Generate ${quantity} Card${quantity > 1 ? 's' : ''}`}
        </button>
        
        {error && (
          <div style={{ marginTop: '20px', padding: '15px', background: '#fc8181', borderRadius: '8px', color: 'white' }}>
            ❌ {error}
          </div>
        )}
      </div>

      {generatedCards.length > 0 && (
        <div>
          <h2 style={{ color: '#2d3748' }}>✅ Generated Cards ({generatedCards.length})</h2>
          <p style={{ color: '#e53e3e', fontWeight: 'bold' }}>⚠️ Save these cards immediately! PINs are only shown once.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>
            {generatedCards.map((card, index) => (
              <div key={index} style={{ background: 'white', border: '2px solid #667eea', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', marginTop: 0 }}>
                  ${card.valueUSD || card.denomination} Card
                  {card.isCustom && <span style={{ marginLeft: '10px', fontSize: '12px', background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: '4px' }}>CUSTOM</span>}
                </h3>
                <div style={{ marginBottom: '15px' }}>
                  <strong style={{ color: '#2d3748' }}>Card Number:</strong>
                  <div style={{ background: '#f7fafc', padding: '10px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '18px', marginTop: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#2d3748' }}>
                    <span>{card.cardNumber}</span>
                    <button onClick={() => copyToClipboard(card.cardNumber)} style={{ padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Copy</button>
                  </div>
                  {card.isCustom && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                      🎲 Random 16-digit number (no prefix)
                    </div>
                  )}
                </div>
                <div>
                  <strong style={{ color: '#2d3748' }}>PIN:</strong>
                  <div style={{ background: '#fff5f5', padding: '10px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '24px', fontWeight: 'bold', marginTop: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#e53e3e' }}>
                    <span>{card.pin}</span>
                    <button onClick={() => copyToClipboard(card.pin)} style={{ padding: '5px 10px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Copy</button>
                  </div>
                </div>
                <div style={{ marginTop: '15px', fontSize: '12px', color: '#718096' }}>
                  <div>Status: {card.status || 'generated'}</div>
                  {card.isCustom && (
                    <div style={{ marginTop: '4px', color: '#f59e0b', fontWeight: 'bold' }}>
                      ⚡ Custom denomination - fully random card number
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setGeneratedCards([])} style={{ marginTop: '30px', padding: '10px 20px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Clear Cards (Can't be recovered!)</button>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;